import { useEffect } from 'react';
import { Paper, PaperProps, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceAdminFragmentFragment } from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { replaceNullOrUndefined } from '../../../utils/object';
import { fromCentesimal } from '../../../utils/transform';
import { InvoiceForm } from '../../forms/invoice/InvoiceForm';
import { InvoiceFormType } from '../../forms/invoice/types';

type Props = {
  invoice?: InvoiceAdminFragmentFragment;
  isLoading?: boolean;
  isError?: boolean;
} & PaperProps;

export const InvoiceInfoBox = ({ invoice, isError, isLoading, sx, ...rest }: Props) => {
  const { t } = useTranslation();
  const { control, reset, setValue } = useForm<InvoiceFormType>({
    defaultValues: replaceNullOrUndefined({
      ...invoice,
      amount: fromCentesimal(invoice?.amount ?? 0),
      amountDue: fromCentesimal(invoice?.amountDue ?? 0),
      currency: invoice?.currency?.toLowerCase() ?? 'czk',
    }),
  });

  useEffect(() => {
    if (invoice) {
      reset(
        replaceNullOrUndefined({
          ...invoice,
          amount: fromCentesimal(invoice?.amount ?? 0),
          amountDue: fromCentesimal(invoice?.amountDue ?? 0),
          currency: invoice.currency?.toLowerCase() ?? 'czk',
        }),
      );
    }
  }, [invoice, reset]);

  return (
    <Paper sx={{ p: { md: 4, xs: 2 }, position: 'relative', minHeight: 270, ...sx }} {...rest}>
      {isLoading ? <FullSizeLoader /> : null}
      {isError ? (
        <ExpandedBox sx={{ backdropFilter: 'blur(2px)', zIndex: 10 }}>
          <Typography>{t('query.error.default')}</Typography>
        </ExpandedBox>
      ) : null}
      <Typography mb={4} variant={'h2'}>
        {t('invoice.info')}
      </Typography>
      <InvoiceForm
        readonly={true}
        control={control}
        highlightAreas={false}
        selectedCurrency={invoice?.currency ?? 'czk'}
        setValue={setValue}
      />
    </Paper>
  );
};
